/**
 * @license
 *
 * Copyright IBM Corp. 2021, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './filter-panel.scss';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * The heading content of filter panel.
 *
 * @element c4d-filter-panel-heading
 */
@customElement(`${c4dPrefix}-filter-panel-heading`)
class C4DFilterPanelHeading extends StableSelectorMixin(LitElement) {
  /**
   * The shadow slot this heading content should be in.
   */
  @property({ reflect: true })
  slot = 'heading';

  connectedCallback() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'heading');
    }
    if (!this.hasAttribute('aria-level')) {
      this.setAttribute('aria-level', '1');
    }
    super.connectedCallback();
  }

  protected _handleSlotChange = () => {
    const { eventChange } = this.constructor as typeof C4DFilterPanelHeading;
    this.dispatchEvent(
      new CustomEvent(eventChange, {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  };

  render() {
    return html` <slot @slotchange=${this._handleSlotChange}></slot> `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--filter-panel-heading`;
  }

  /**
   * The event name when this element changes.
   */
  static get eventChange() {
    return `${c4dPrefix}-filter-panel-heading-change`;
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static styles = styles;
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DFilterPanelHeading;
